/**
 * CloseCartModal — Wave 6.2 cart-resolution UI.
 *
 * Opens from the kebab menu on each in-flight cart card. Renders only the
 * resolution codes valid for the cart's `(originRole, hasRegulatedArtifact)`
 * tuple per the per-origin validity matrix, requires a ≥ 5-char reason, and
 * submits to `client.retail.carts.resolve`.
 *
 *   - 409 LOBBY_TICKET_ACTIVE / 409 EXTERNAL_COMMIT_LOCK render inline as a
 *     modal-banner error (not a toast) so the FSR can read why the close
 *     was rejected without losing the modal state.
 *   - On success, the parent removes the card and toasts a confirmation.
 *
 * Visuals reuse the global ModalShell + Button + Pill primitives + design
 * tokens. No new colors, no inline color literals.
 */

import { useEffect, useId, useMemo, useState } from 'react';
import { Button, ModalShell, Pill } from '@/components/primitives';
import type {
  CartDto,
  CartProductResolutionInput,
  CartResolutionCode,
  ResolveCartResponse,
  SystemErrorCode,
} from '@retail-ops/shared-types';
import { ApiError } from '@retail-ops/api-client';
import { useApi } from './hooks/useApi';
import { summarizePrimaryApplicant } from './utils/cartCardData';
import {
  MIN_REASON_LENGTH,
  getValidResolutionOptions,
  humanizeOriginTier,
} from './utils/cartResolutionMatrix';
import { TestModeBadge } from '@/modules/_shared/TestModeBadge';
import { PerProductResolutionPicker } from './components/PerProductResolutionPicker';
import { useConfig } from '@/contexts/ConfigContext';

export interface CloseCartModalProps {
  open: boolean;
  cart: CartDto;
  onClose: () => void;
  /** Called when the cart was successfully resolved (parent removes card + toasts). */
  onResolved: (cart: CartDto, resolutionCode: CartResolutionCode) => void;
}

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'inline-error'; message: string }
  | { kind: 'fatal-error'; message: string };

export function CloseCartModal({
  open,
  cart,
  onClose,
  onResolved,
}: CloseCartModalProps): JSX.Element {
  const { client, makeSignal } = useApi();
  const { config } = useConfig();

  const options = useMemo(
    () => getValidResolutionOptions(cart.originRole, cart.hasRegulatedArtifact),
    [cart.originRole, cart.hasRegulatedArtifact],
  );

  const applicant = summarizePrimaryApplicant(cart);
  const humanTitle = applicant.isEmpty
    ? 'this empty draft'
    : `the application for ${applicant.label}`;

  // Pre-select the only valid code when the matrix produces exactly one option.
  const [selectedCode, setSelectedCode] = useState<CartResolutionCode | null>(
    options.length === 1 && options[0] ? options[0].code : null,
  );
  const [reason, setReason] = useState<string>('');
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: 'idle' });
  // Wave 6.3 — System-Error errorCode + per-product divergent rows.
  const [errorCode, setErrorCode] = useState<string>('');
  const [errorCodes, setErrorCodes] = useState<ReadonlyArray<SystemErrorCode>>([]);
  const [productRows, setProductRows] = useState<ReadonlyArray<CartProductResolutionInput>>([]);

  // Lazy-load the SystemErrorCodes catalog once when the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    client.retail.config.systemErrorCodes({ signal: makeSignal() }).then(
      (res) => {
        if (cancelled) return;
        const anyRes = res as unknown as { success?: boolean; data?: SystemErrorCode[] };
        if (anyRes.success !== false) {
          setErrorCodes(anyRes.data ?? []);
        }
      },
      () => {
        // Non-fatal: dropdown stays empty and the FSR can still pick System-Error
        // (backend will validate); error is cosmetic for the closed catalog.
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset on re-open so re-using the modal for a different cart is clean.
  useEffect(() => {
    if (!open) return;
    setSelectedCode(options.length === 1 && options[0] ? options[0].code : null);
    setReason('');
    setErrorCode('');
    setProductRows([]);
    setSubmitState({ kind: 'idle' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cart.cartId]);

  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= MIN_REASON_LENGTH;
  const errorCodeValid = selectedCode !== 'System-Error' || errorCode.length > 0;
  const canSubmit =
    selectedCode !== null
    && reasonValid
    && errorCodeValid
    && submitState.kind !== 'submitting';

  const reasonId = useId();
  const errorCodeId = useId();

  async function handleSubmit(): Promise<void> {
    if (!selectedCode || !reasonValid) return;
    if (selectedCode === 'System-Error' && !errorCode) return;
    setSubmitState({ kind: 'submitting' });
    try {
      // Build payload — only send divergent product rows.
      const divergent = productRows.filter((r) => r.resolutionCode !== selectedCode);
      const body: Parameters<typeof client.retail.carts.resolve>[1] = {
        resolutionCode: selectedCode,
        resolutionReason: trimmedReason,
      };
      if (selectedCode === 'System-Error') body.errorCode = errorCode;
      if (divergent.length > 0) body.productResolutions = divergent;

      const res: ResolveCartResponse = await client.retail.carts.resolve(
        cart.cartId,
        body,
        { signal: makeSignal() },
      );
      if (!res.success) {
        const code = res.errorCode ?? '';
        const isInline = code === 'LOBBY_TICKET_ACTIVE' || code === 'EXTERNAL_COMMIT_LOCK';
        setSubmitState({
          kind: isInline ? 'inline-error' : 'fatal-error',
          message: res.error ?? 'Unable to close the cart.',
        });
        return;
      }
      const resolved: CartDto = res.cart ?? {
        ...cart,
        resolutionCode: selectedCode,
        resolutionReason: trimmedReason,
      };
      onResolved(resolved, selectedCode);
      onClose();
    } catch (err) {
      if (ApiError.isApiError(err)) {
        const isInline =
          err.code === 'LOBBY_TICKET_ACTIVE' || err.code === 'EXTERNAL_COMMIT_LOCK';
        setSubmitState({
          kind: isInline ? 'inline-error' : 'fatal-error',
          message: err.message,
        });
        return;
      }
      if (err instanceof Error && err.name === 'AbortError') return;
      setSubmitState({
        kind: 'fatal-error',
        message: err instanceof Error ? err.message : 'Unexpected error.',
      });
    }
  }

  const noOptions = options.length === 0;

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Close cart"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={!canSubmit || noOptions}
          >
            {submitState.kind === 'submitting' ? 'Closing…' : 'Close cart'}
          </Button>
        </>
      }
    >
      <div style={{ fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.5 }}>
        You're about to close <strong>{humanTitle}</strong>
        {cart.originRole && (
          <>
            {' '}
            <Pill variant="info">{humanizeOriginTier(cart.originRole)}</Pill>
          </>
        )}
        {cart.hasRegulatedArtifact === true && (
          <>
            {' '}
            <Pill variant="warn">Has documents</Pill>
          </>
        )}
        {cart.isTestMode === true && (
          <>
            {' '}
            <TestModeBadge active size="sm" />
          </>
        )}
        . Choose the resolution code that best matches the outcome.
      </div>

      {noOptions && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-5)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            borderRadius: 'var(--r-md)',
            fontSize: 13,
          }}
        >
          No resolution codes are valid for this cart's origin and artifact state. The cart
          may be locked by another workflow — refresh the dashboard and try again.
        </div>
      )}

      {!noOptions && (
        <div
          role="radiogroup"
          aria-label="Resolution code"
          style={{
            display: 'grid',
            gap: 'var(--sp-4)',
          }}
        >
          {options.map((opt) => {
            const checked = selectedCode === opt.code;
            return (
              <label
                key={opt.code}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--sp-4)',
                  padding: 'var(--sp-5)',
                  border: `1px solid ${checked ? 'var(--pcb-blue)' : 'var(--surface-border)'}`,
                  borderRadius: 'var(--r-md)',
                  background: checked ? 'var(--info-bg)' : 'var(--surface-card)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="resolution-code"
                  value={opt.code}
                  checked={checked}
                  onChange={() => setSelectedCode(opt.code)}
                  style={{ marginTop: 3 }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: 'var(--ink-900)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sp-3)',
                    }}
                  >
                    {opt.label}
                    {opt.tone === 'danger' && <Pill variant="err">Compliance review</Pill>}
                    {opt.tone === 'positive' && <Pill variant="ok">Terminal success</Pill>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>{opt.description}</div>
                </div>
              </label>
            );
          })}
        </div>
      )}

      {selectedCode === 'System-Error' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <label
            htmlFor={errorCodeId}
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.6px',
              color: 'var(--ink-500)',
            }}
          >
            System error code <span style={{ color: 'var(--pcb-red)' }}>*</span>
          </label>
          <select
            id={errorCodeId}
            value={errorCode}
            onChange={(e) => setErrorCode(e.target.value)}
            style={{
              width: '100%',
              padding: 'var(--sp-3) var(--sp-4)',
              border: '1px solid var(--ink-300)',
              borderRadius: 'var(--r-md)',
              background: 'var(--surface-card)',
              fontSize: 13,
            }}
          >
            <option value="">Select the failing system / component...</option>
            {errorCodes
              .filter((ec) => ec.isActive)
              .map((ec) => (
                <option key={ec.code} value={ec.code}>
                  {ec.label}
                  {ec.sourceSystem ? ` — ${ec.sourceSystem}` : ''}
                </option>
              ))}
          </select>
          {(() => {
            const found = errorCodes.find((ec) => ec.code === errorCode);
            if (!found?.suggestedNextAction) return null;
            return (
              <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>
                <strong>Next step:</strong> {found.suggestedNextAction}
              </div>
            );
          })()}
        </div>
      )}

      <PerProductResolutionPicker
        cart={cart}
        products={config.products ?? []}
        cartLevelCode={selectedCode}
        cartLevelErrorCode={errorCode || undefined}
        errorCodes={errorCodes}
        rows={productRows}
        onChange={setProductRows}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <label
          htmlFor={reasonId}
          style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
            color: 'var(--ink-500)',
          }}
        >
          Reason <span style={{ color: 'var(--pcb-red)' }}>*</span>
        </label>
        <textarea
          id={reasonId}
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={
            selectedCode === 'Adverse-Action'
              ? 'E.g. AA-02 Insufficient Credit References'
              : selectedCode === 'Withdrawn'
                ? 'E.g. Customer decided not to proceed today.'
                : selectedCode === 'Cancelled'
                  ? 'E.g. Test draft, no applicant captured.'
                  : 'Document the reason this cart is being closed.'
          }
          style={{
            width: '100%',
            padding: 'var(--sp-4)',
            border: '1px solid var(--ink-300)',
            borderRadius: 'var(--r-md)',
            fontFamily: 'var(--font-sans)',
            fontSize: 13,
            resize: 'vertical',
          }}
        />
        <div style={{ fontSize: 11, color: reasonValid ? 'var(--ink-500)' : 'var(--pcb-red)' }}>
          {reasonValid
            ? `${trimmedReason.length} characters`
            : `At least ${MIN_REASON_LENGTH} characters required (${trimmedReason.length} so far).`}
        </div>
      </div>

      {submitState.kind === 'inline-error' && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-5)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            borderRadius: 'var(--r-md)',
            fontSize: 13,
            border: '1px solid var(--pcb-red)',
          }}
        >
          <strong>Cart cannot be closed right now.</strong>
          <div style={{ marginTop: 'var(--sp-2)' }}>{submitState.message}</div>
          <div style={{ marginTop: 'var(--sp-3)', fontSize: 12, color: 'var(--ink-700)' }}>
            Resolve the active lobby ticket or external commit lock, then retry.
          </div>
        </div>
      )}

      {submitState.kind === 'fatal-error' && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-5)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            borderRadius: 'var(--r-md)',
            fontSize: 13,
          }}
        >
          {submitState.message}
        </div>
      )}
    </ModalShell>
  );
}
